import { defineStore } from 'pinia';
import { billingService } from '~/services/billing-service';
import { memberFacingMessage } from '~/utils/errors';
import type { Invoice } from '~/types/person/financial';

export const useBillingStore = defineStore('billing', () => {
    const personStore = usePersonStore();
    const authStore = useAuthStore();

    // State
    const invoices = ref<Invoice[]>([]);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const totalItems = ref(0);
    const itemsPerPage = ref(10);
    const isLoading = ref(false);
    const error = ref<string | null>(null);
    const creditBalance = ref<number>(0);
    const isCreditBalanceLoading = ref(false);
    const { showSuccess, showError, showWarning } = useGlobalToast();

    // Actions
    async function fetchInvoices(page: number = 1) {
        if (!personStore.person) {
            error.value = 'User not found';
            return;
        }

        isLoading.value = true;
        error.value = null;

        try {
            const response = await billingService.getInvoices(personStore.person.id, page, itemsPerPage.value);

            invoices.value = response.data;
            currentPage.value = response.current_page;
            totalPages.value = response.last_page;
            totalItems.value = response.total;
        } catch (err: unknown) {
            error.value = memberFacingMessage(err, 'Failed to fetch invoices');
            showError("Error", error.value!);
        } finally {
            isLoading.value = false;
        }
    }

    async function downloadInvoice(invoiceId: string): Promise<void> {
        if (!personStore.person) {
            error.value = 'User not found';
            return;
        }
        try {
            const blob = await billingService.downloadInvoice(personStore.person.id, invoiceId);

            // Create download link
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice-${invoiceId}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (err: unknown) {
            showError("Error", memberFacingMessage(err, 'Failed to download invoice'));
        }
    }

    // Fetch the invoice PDF (the API streams it inline as application/pdf) and return an object
    // URL the caller can open in a new tab for viewing. The caller owns revoking the URL.
    async function getInvoiceViewUrl(invoiceId: string): Promise<string> {
        if (!personStore.person) {
            throw new Error('User not found');
        }
        const blob = await billingService.downloadInvoice(personStore.person.id, invoiceId);
        return URL.createObjectURL(blob);
    }

    async function fetchCreditBalance(): Promise<void> {
        if (!personStore.person) {
            error.value = 'User not found';
            return;
        }

        isCreditBalanceLoading.value = true;
        error.value = null;

        try {
            const balance = await billingService.getCreditBalance(personStore.person.id);
            creditBalance.value = balance;
        } catch (err: unknown) {
            error.value = memberFacingMessage(err, 'Failed to fetch credit balance');
            showError("Error", error.value!);
        } finally {
            isCreditBalanceLoading.value = false;
        }
    }

    async function purchaseGiftCertificate(name: string, email: string, amount: number, paymentMethodId?: string): Promise<any> {
        if (!personStore.person) {
            error.value = 'User not found';
            return;
        }

        error.value = null;

        try {
            const result = await billingService.purchaseGiftCertificate(personStore.person.id, name, email, amount, paymentMethodId);
            showSuccess("Success", 'Gift certificate purchased successfully!');
            fetchInvoices()
            authStore.getMe();
            return result;
        } catch (err: unknown) {
            error.value = memberFacingMessage(err, 'Failed to purchase gift certificate');
            showError("Error", error.value!);
            throw err;
        }
    }

    async function applyGiftCertificate(code: string): Promise<any> {
        if (!personStore.person) {
            error.value = 'User not found';
            return;
        }

        error.value = null;

        try {
            const result = await billingService.applyGiftCertificate(personStore.person.id, code);
            showSuccess("Success", 'Gift certificate applied successfully!');
            fetchInvoices()
            authStore.getMe();
            return result;
        } catch (err: unknown) {
            error.value = memberFacingMessage(err, 'Failed to apply gift certificate');
            showError("Error", error.value!);
            throw err;
        }
    }

    async function sendGiftCertificateEmail(recipientName: string, recipientEmail: string, code: string): Promise<any> {
        if (!personStore.person) {
            error.value = 'User not found';
            return;
        }

        error.value = null;

        try {
            const result = await billingService.sendGiftCertificateEmail(personStore.person.id, recipientName, recipientEmail, code);
            showSuccess("Success", 'Gift certificate email sent successfully!');
            return result;
        } catch (err: unknown) {
            error.value = memberFacingMessage(err, 'Failed to send gift certificate email');
            showError("Error", error.value!);
            throw err;
        }
    }

    // Computed
    const hasInvoices = computed(() => invoices.value.length > 0);
    const paginationInfo = computed(() => ({
        currentPage: currentPage.value,
        totalPages: totalPages.value,
        totalItems: totalItems.value,
        itemsPerPage: itemsPerPage.value,
    }));

    return {
        // State
        invoices,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        isLoading,
        error,
        creditBalance,
        isCreditBalanceLoading,

        // Actions
        fetchInvoices,
        downloadInvoice,
        getInvoiceViewUrl,
        fetchCreditBalance,
        purchaseGiftCertificate,
        applyGiftCertificate,
        sendGiftCertificateEmail,

        // Computed
        hasInvoices,
        paginationInfo,
    };
});
